import React, { useCallback, useState } from 'react';
import { STATUS_TYPE } from '../global/constants';
import { StatusListInterface } from '../interfaces/ProjectInterface';
import { TaskModel } from '../mobx/models/task';
import { captureException } from '../services/logService';
import { checkSubtaskStatus, dispatchTaskUpdates, onUpdateStatus } from '../services/taskActionService';
import Modal from '../component/modals/modal';
import ConfirmationModal from '../component/modals/confirmationModal';

interface UseTaskActionsResult {
  onChangeStatus: (task: TaskModel, status: StatusListInterface, previousStatus: StatusListInterface) => void;
  onClickTaskCheckBox: (task: TaskModel, status: boolean) => void;
  confirmationModal: React.ReactElement;
}

export function useTaskActions(loadData?: () => void): UseTaskActionsResult {
  const [status, setStatus] = useState<StatusListInterface>();
  const [previousStatus, setPreviousStatus] = useState<StatusListInterface>();
  const [currentTask, setCurrentTask] = useState<TaskModel>();
  const [isOpen, setIsOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  const openModal = useCallback(() => setIsOpen(true), []);
  const closeModal = useCallback(() => setIsOpen(false), []);

  const onChangeStatus = useCallback(
    (task: TaskModel, nextStatus: StatusListInterface, prevStatus: StatusListInterface) => {
      const isSubtaskRemaining = checkSubtaskStatus(task);
      if (nextStatus.Type === STATUS_TYPE.COMPLETED && !isSubtaskRemaining) {
        openModal();
        setStatus(nextStatus);
        setPreviousStatus(prevStatus);
        setCurrentTask(task);
        return;
      }
      onUpdateStatus(task, nextStatus, prevStatus, false);
    },
    [openModal]
  );

  const onClickTaskCheckBox = useCallback(
    (task: TaskModel, nextStatus: boolean) => {
      const isSubtaskRemaining = checkSubtaskStatus(task);
      if (nextStatus && !isSubtaskRemaining) {
        openModal();
        setCurrentTask(task);
        return;
      }
      task.setStatus(nextStatus, false);
      dispatchTaskUpdates(task, nextStatus);
      loadData?.();
    },
    [openModal, loadData]
  );

  const onConfirm = useCallback(() => {
    if (!currentTask) return;
    try {
      setConfirmLoading(true);
      if (currentTask.project?.statusEnable && status && previousStatus) {
        onUpdateStatus(currentTask, status, previousStatus, true);
      } else {
        currentTask.setStatus(true, true);
        dispatchTaskUpdates(currentTask, true);
        loadData?.();
      }
    } catch (error) {
      captureException(error);
    } finally {
      setConfirmLoading(false);
      closeModal();
    }
  }, [closeModal, currentTask, loadData, previousStatus, status]);

  const onCancel = useCallback(() => {
    if (!currentTask) return;
    try {
      setCancelLoading(true);
      if (currentTask.project?.statusEnable && status && previousStatus) {
        onUpdateStatus(currentTask, status, previousStatus, false);
      } else {
        currentTask.setStatus(true, false);
        dispatchTaskUpdates(currentTask, true);
        loadData?.();
      }
    } catch (error) {
      captureException(error);
    } finally {
      setCancelLoading(false);
      closeModal();
    }
  }, [closeModal, currentTask, loadData, previousStatus, status]);

  const confirmationModal = (
    <Modal open={isOpen} onClose={closeModal} maxWidth={'334px'}>
      <ConfirmationModal
        loading={confirmLoading}
        cancelLoading={cancelLoading}
        onOk={onConfirm}
        onNo={onCancel}
        onClose={closeModal}
        modalTitle='Uncompleted Subtasks'
        description='Do you want to complete all these subtasks?'
      />
    </Modal>
  );

  return { onChangeStatus, onClickTaskCheckBox, confirmationModal };
}
